import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Share2, ExternalLink, Youtube, Instagram, Mail, Globe, Apple } from 'lucide-react';
import '../styles/LinksPage.css';

const affirmations = [
  "I am at peace",
  "I am capable of amazing things",
  "I choose positivity",
  "I am grateful",
  "I radiate positive energy"
];

const links = [
  { text: "Get the App", url: "https://apps.apple.com/us/app/innertap/id6752519125", icon: Apple },
  { text: "InnerTap Website", url: "https://innertap.com", icon: Globe },
  { text: "YouTube", url: "https://youtube.com/@innertapapp", icon: Youtube },
  { text: "Instagram", url: "https://instagram.com/innertapapp", icon: Instagram },
  { text: "TikTok", url: "https://tiktok.com/@innertapapp", icon: ExternalLink },
];

const LinksPage = () => {
  const [affirmationIndex, setAffirmationIndex] = useState(0);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isServerReady, setIsServerReady] = useState(false);

  // Rotate affirmations
  useEffect(() => {
    const timer = setInterval(() => {
      setAffirmationIndex((prev) => (prev + 1) % affirmations.length);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  // Check server health
  useEffect(() => {
    const checkServer = async () => {
      try {
        const response = await fetch('/.netlify/functions/subscribe', {
          method: 'OPTIONS',
        });
        if (response.ok || response.status === 204) {
          setIsServerReady(true);
        }
      } catch (err) {
        console.log('Server check failed, will retry...');
      }
    };

    checkServer();
    const interval = setInterval(checkServer, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Check out InnerTap',
          text: 'Hey, You should check out this app called InnerTap. It\'s like a mix of meditation and affirmations you tap through at your own pace. Thought you might love it 💫',
          url: 'https://innertap.com',
        });
      } else {
        await navigator.clipboard.writeText('https://innertap.com');
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isServerReady) {
      setError('Please wait for server connection...');
      return;
    }
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/.netlify/functions/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
        setEmail('');
      } else {
        setError(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="links-page">
      <div className="links-container">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="links-card"
        >
          {/* Share Button */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleShare}
            className="share-button"
          >
            <Share2 size={20} />
          </motion.button>

          {/* Logo */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="links-header"
          >
            <div className="links-logo-container">
              <img
                src="/innertap-logo.png"
                alt="InnerTap Logo"
                className="links-logo"
              />
            </div>

            {/* Affirmation Display */}
            <div className="affirmation-container">
              <AnimatePresence mode="wait">
                <motion.p
                  key={affirmationIndex}
                  initial={{ opacity: 0, y: 25 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -25 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="affirmation-text"
                >
                  {affirmations[affirmationIndex]}
                </motion.p>
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Link Buttons */}
          <div className="links-list">
            {links.map((link, index) => (
              <motion.a
                key={index}
                href={link.comingSoon ? undefined : link.url}
                target={link.comingSoon ? undefined : "_blank"}
                rel="noopener noreferrer"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`link-button ${link.comingSoon ? 'coming-soon' : ''}`}
                onClick={link.comingSoon ? (e) => e.preventDefault() : undefined}
              >
                <div className="link-content">
                  <span className="link-icon">
                    <link.icon size={20} />
                  </span>
                  <span className="link-text">
                    {link.text}
                    {link.comingSoon && <span className="coming-soon-badge">Coming Soon</span>}
                  </span>
                </div>
                {!link.comingSoon && (
                  <motion.div whileHover={{ x: 5 }} className="link-arrow">
                    <ExternalLink size={18} />
                  </motion.div>
                )}
              </motion.a>
            ))}
          </div>

          {/* Email Signup */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="email-section"
          >
            {!isSubmitted ? (
              <>
                <h3 className="email-heading">
                  <Mail size={18} style={{ marginRight: '8px' }} />
                  Get early access & exclusive content
                </h3>
                <form onSubmit={handleSubmit} className="email-form">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    disabled={isLoading || !isServerReady}
                    className="email-input"
                  />
                  <button
                    type="submit"
                    disabled={isLoading || !isServerReady}
                    className="email-button"
                  >
                    {isLoading ? 'Joining...' : !isServerReady ? 'Connecting...' : 'I AM Ready'}
                  </button>
                </form>
                {error && <p className="error-text">{error}</p>}
                <p className="privacy-note">
                  We guard your email like a meditation master guards inner peace.
                </p>
              </>
            ) : (
              <div className="success-message">
                <p>You're in! Get ready to transform your meditation practice.</p>
              </div>
            )}
          </motion.div>

          {/* About Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="about-section"
          >
            <p>
              InnerTap is a mix of meditation and affirmations. You tap through at your own pace.
              It uses sound, sight, and touch to create a transformative experience.
            </p>
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="links-footer"
          >
            <p>&copy; 2024 InnerTap - Tap into your inner self</p>
            <a href="/legal" className="legal-link">Terms & Privacy</a>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default LinksPage;
